import { useMemo, useState } from 'react'
import {
  fetchHostPubkey,
  fetchResourceConnections,
  putSecret,
  setConnectionCredential,
  updateResourceConnection,
} from '../../api/ws'
import { base64Decode, base64Encode, encryptToHostPubkey } from '../../lib/secretEnvelope'
import { useAppStore } from '../../store/app'
import type { ResourceConnection } from '../../store/types'
import {
  buildConfigJSON,
  ConfigStep,
  OAuthConnectStep,
  oauthProviderFromSpecs,
  parseConfigSchema,
  parseCredentialSpecs,
} from './wizardParts'

// ConnectionAccounts is the account panel for a resource connection: which
// upstream account it authenticates as, and how to connect, re-authorize,
// or disconnect it.
//
// A connection authenticates as exactly ONE account. To use a second
// account, make a second connection — that is not a limitation, it is what
// makes "who can use this?" answerable: rules are keyed on the connection,
// and an agent can only hold one binding per connection anyway.
//
// Re-authorizing is deliberately not a distinct mode. Running the OAuth
// dance again against this connection rewrites the same secret's ciphertext
// in place, so every agent that already has access keeps it — nothing to
// re-point, no re-approval.

export function ConnectionAccounts({ connection }: { connection: ResourceConnection }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [oauthOpen, setOAuthOpen] = useState(false)

  // The provider slug comes from the plugin's own credential spec
  // (`type: oauth2_<provider>`). No OAuth credential → no connect affordance
  // (the credential was pasted through the wizard instead).
  const installs = useAppStore((s) => s.pluginInstalls)
  const provider = useMemo(() => {
    const install = installs.find((p) => p.id === connection.plugin_install_id)
    return oauthProviderFromSpecs(parseCredentialSpecs(install?.credential_specs))
  }, [installs, connection.plugin_install_id])
  const canConnect = !!provider && !!connection.host_id
  const authorized = !!connection.authorized

  async function disconnect() {
    if (
      !confirm(
        'Disconnect this account? Agents using this connection lose access on their next call.',
      )
    )
      return
    setBusy(true)
    setError(null)
    try {
      const resp = await setConnectionCredential({
        resourceConnectionID: connection.id,
        secretID: '',
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      fetchResourceConnections()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      <h4
        style={{
          margin: '0 0 8px',
          fontSize: 13,
          opacity: 0.7,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Account
      </h4>

      {error && (
        <div
          style={{
            padding: '6px 10px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '8px 10px',
          background: '#fafafa',
          borderRadius: 4,
          border: '1px solid #eee',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {authorized ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Connected</div>
              {connection.external_identity_label ? (
                <div style={{ fontSize: 12, opacity: 0.6 }}>
                  as {connection.external_identity_label}
                </div>
              ) : null}
            </>
          ) : (
            <div style={{ fontSize: 13, opacity: 0.6 }}>Not connected to an account yet.</div>
          )}
        </div>
        {authorized && (
          <button
            style={{ fontSize: 11, color: '#900', padding: '2px 8px' }}
            disabled={busy}
            onClick={disconnect}
          >
            Disconnect
          </button>
        )}
      </div>

      {/* Non-OAuth (token/credential) connections: set or replace the stored
          secret in place. Previously the ONLY way to file a credential was the
          Connect-an-account wizard, so a connection whose credential was never
          bound (or got unbound) was a dead end. */}
      {!provider && <CredentialManager connection={connection} />}

      {/* Edit the connection's non-sensitive config (e.g. an HA host URL) after
          creation. Previously config could only be set in the create wizard, so a
          bad or mDNS-flaky ha_url was a dead end — the connection existed but
          every call failed and there was no way to point it at a stable IP. */}
      <ConnectionConfigManager connection={connection} />

      {canConnect && !oauthOpen && (
        <button style={{ marginTop: 10, fontSize: 13 }} disabled={busy} onClick={() => setOAuthOpen(true)}>
          {authorized ? 'Re-authorize' : 'Connect an account'}
        </button>
      )}

      {oauthOpen && canConnect && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #ddd', borderRadius: 6 }}>
          <OAuthConnectStep
            connectionID={connection.id}
            hostID={connection.host_id!}
            provider={provider}
            pluginDisplayName={connection.display_name || connection.slug || 'this account'}
            connectLabel={authorized ? 'Re-authorize with Google' : 'Connect with Google'}
            helpText={
              authorized
                ? 'Sign in again to refresh this account. Every agent that already has access keeps it — nothing to re-point.'
                : 'Sign in to connect this account. Then choose which agents can use it from "Who can use this?".'
            }
            onConnected={() => {
              setOAuthOpen(false)
              fetchResourceConnections()
            }}
            onError={setError}
          />
          <button style={{ marginTop: 8, fontSize: 13 }} onClick={() => setOAuthOpen(false)}>
            Cancel
          </button>
        </div>
      )}
    </section>
  )
}

// CredentialManager lets the household set or replace a token-style connection's
// stored secret(s) directly on an existing connection — the affordance the account
// panel above was missing for non-OAuth plugins. Each secret is sealed to the
// connection's host and filed under the manifest's credential name (same path the
// wizard uses), so autofill picks it up on the agent's next invoke.
function CredentialManager({ connection }: { connection: ResourceConnection }) {
  const installs = useAppStore((s) => s.pluginInstalls)
  const install = useMemo(
    () => installs.find((p) => p.id === connection.plugin_install_id),
    [installs, connection.plugin_install_id],
  )
  const secretSpecs = useMemo(
    () => parseCredentialSpecs(install?.credential_specs).filter((s) => s.secret),
    [install],
  )

  const [values, setValues] = useState<Map<string, string>>(new Map())
  const [busy, setBusy] = useState<string | null>(null) // the credential name being saved
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  if (secretSpecs.length === 0) return null

  async function save(name: string) {
    const plaintext = (values.get(name) ?? '').trim()
    if (!plaintext) return
    if (!connection.host_id) {
      setError('This connection has no host to encrypt the credential to.')
      return
    }
    setBusy(name)
    setError(null)
    setSaved(null)
    try {
      const pubResp = await fetchHostPubkey(connection.host_id)
      if (pubResp.error || !pubResp.pubkey) {
        setError(
          pubResp.error ??
            'This host has no encryption key yet. Bring its daemon online, then try again.',
        )
        return
      }
      const envelope = encryptToHostPubkey(
        base64Decode(pubResp.pubkey),
        new TextEncoder().encode(plaintext),
      )
      const putResp = await putSecret({
        hostID: connection.host_id,
        name: `${connection.slug}.${name}`,
        ciphertextB64: base64Encode(envelope),
        purpose: `${name} for ${connection.id}`,
      })
      if (putResp.error || !putResp.id) {
        setError(`Storing the secret failed: ${putResp.error ?? 'no id returned'}`)
        return
      }
      const credResp = await setConnectionCredential({
        resourceConnectionID: connection.id,
        secretID: putResp.id,
        name,
      })
      if (credResp.error) {
        setError(`Binding the credential failed: ${credResp.error}`)
        return
      }
      setValues((m) => {
        const next = new Map(m)
        next.delete(name)
        return next
      })
      setSaved(name)
      fetchResourceConnections()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <section style={{ marginTop: 16 }}>
      <h4
        style={{
          margin: '0 0 8px',
          fontSize: 13,
          opacity: 0.7,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Credentials
      </h4>

      {error && (
        <div
          style={{
            padding: '6px 10px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {saved && (
        <div
          style={{
            padding: '6px 10px',
            background: '#e6ffed',
            color: '#046b2f',
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 12,
          }}
        >
          Saved “{saved}”. Agents using this connection pick it up on their next call.
        </div>
      )}

      {secretSpecs.map((spec) => (
        <div key={spec.name} style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>{spec.name}</label>
          {spec.description && (
            <div style={{ fontSize: 11, opacity: 0.6, margin: '2px 0 4px' }}>{spec.description}</div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="password"
              autoComplete="off"
              value={values.get(spec.name) ?? ''}
              onChange={(e) =>
                setValues((m) => new Map(m).set(spec.name, e.target.value))
              }
              placeholder={connection.authorized ? 'Replace token…' : 'Paste token…'}
              style={{ flex: 1, fontSize: 16, minWidth: 0 }}
            />
            <button
              disabled={busy !== null || !(values.get(spec.name) ?? '').trim()}
              onClick={() => save(spec.name)}
              style={{ fontSize: 13 }}
            >
              {busy === spec.name ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ))}

      <div style={{ fontSize: 11, opacity: 0.6 }}>
        The token is encrypted to this connection’s host and is only ever decrypted
        there — it never reaches Hearth’s servers or the agent in plaintext.
      </div>
    </section>
  )
}

// configToValues seeds the editor's string-keyed field map from a connection's
// stored config JSON, restricted to the keys the schema declares. Booleans and
// numbers stringify to what ConfigStep's inputs expect ('true'/'false', decimal
// text). Unknown/extra keys in the blob are ignored — the schema is the shape.
export function configToValues(
  schema: ReturnType<typeof parseConfigSchema>,
  configJSON: string | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  if (!schema?.properties) return out
  let parsed: Record<string, unknown> = {}
  try {
    parsed = configJSON ? (JSON.parse(configJSON) as Record<string, unknown>) : {}
  } catch {
    parsed = {}
  }
  for (const key of Object.keys(schema.properties)) {
    const v = parsed[key]
    if (v === undefined || v === null) continue
    out[key] = typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v)
  }
  return out
}

// ConnectionConfigManager edits a connection's non-sensitive config after
// creation — the affordance the panel was missing. Its motivating case: an HA
// connection whose ha_url pointed at a flaky .local mDNS name (or just changed)
// was a dead end, since config could only be set in the create wizard. Renders
// nothing for plugins that declare no config schema.
function ConnectionConfigManager({ connection }: { connection: ResourceConnection }) {
  const installs = useAppStore((s) => s.pluginInstalls)
  const install = useMemo(
    () => installs.find((p) => p.id === connection.plugin_install_id),
    [installs, connection.plugin_install_id],
  )
  const schema = useMemo(() => parseConfigSchema(install?.config_schema), [install])

  // Seed once from the stored config; the built form of that seed is the
  // baseline the dirty check compares against, so re-serialization is
  // apples-to-apples.
  const initialValues = useMemo(() => configToValues(schema, connection.config), [schema, connection.config])
  const baselineJSON = useMemo(() => buildConfigJSON(schema, initialValues), [schema, initialValues])

  const [values, setValues] = useState<Record<string, string>>(initialValues)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  if (!schema?.properties || Object.keys(schema.properties).length === 0) return null

  const nextJSON = buildConfigJSON(schema, values)
  const dirty = nextJSON !== baselineJSON

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!dirty) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const resp = await updateResourceConnection({
        id: connection.id,
        // Pass the current display name through: the server's update overwrites
        // display_name unconditionally, so omitting it would blank the name.
        displayName: connection.display_name,
        config: nextJSON,
      })
      if (resp.error) {
        setError(resp.error)
        return
      }
      setSaved(true)
      fetchResourceConnections()
    } catch (err) {
      setError(String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section style={{ marginTop: 16 }}>
      <h4
        style={{
          margin: '0 0 8px',
          fontSize: 13,
          opacity: 0.7,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Configuration
      </h4>

      {error && (
        <div
          style={{
            padding: '6px 10px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {saved && !dirty && (
        <div
          style={{
            padding: '6px 10px',
            background: '#e6ffed',
            color: '#046b2f',
            borderRadius: 4,
            marginBottom: 8,
            fontSize: 12,
          }}
        >
          Saved. Agents on this connection use the new settings on their next call.
        </div>
      )}

      <ConfigStep
        schema={schema}
        values={values}
        onChange={(v) => {
          setValues(v)
          setSaved(false)
        }}
        onNext={save}
        disabled={busy || !dirty}
        submitLabel={busy ? 'Saving…' : 'Save configuration'}
      />

      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>
        These are non-sensitive connection settings (like a host address) — not
        credentials. A changed value reaches the host’s daemon and its next call
        uses it.
      </div>
    </section>
  )
}
